import {
  TANK_DIRECTIONS,
  TEAM_COLORS,
  Theme,
  TICK_MS,
  TILE_SIZE,
  Terrain,
  type BaseState,
  type BuilderState,
  type MineState,
  type PillboxState,
  type PlayerState,
  type ShellState,
  type TileUpdate,
  type MergedWorldSnapshot,
} from "@webolo/shared";
import type { LoadedMap } from "./map.js";
import { buildTileAtlas } from "./tileArt.js";
import {
  BASE_EVIL,
  BASE_GOOD,
  BASE_NEUTRAL,
  EXPLOSION_FRAMES,
  LGM_FRAMES,
  GUNSIGHT_SPRITE,
  MINE_SPRITE,
  PILL_EVIL,
  PILL_GOOD,
  SHELL_SPRITES,
  TANK_EVIL,
  TANK_EVILBOAT,
  TANK_GOOD,
  TANK_GOODBOAT,
  TANK_SELF,
  TANK_SELFBOAT,
  TERRAIN_TILE,
  dirToSpriteIndex,
  loadSpriteSheet,
  loadModernSheet,
} from "./tilesheet.js";
import {
  variantForBoat,
  variantForBuilding,
  variantForDeepSea,
  variantForForest,
  variantForRiver,
  variantForRoad,
  type Neighbors,
} from "./tileVariants.js";
import { ExplosionTracker } from "./explosions.js";
import { SoundSystem } from "./sound.js";
import { SoundEventTracker } from "./soundEvents.js";

const VIEW_W = 960;
const VIEW_H = 720;
// Render scale: each world pixel is drawn SCALE×SCALE screen pixels so the
// viewport matches WinBolo's 15-tile view (MAIN_SCREEN_SIZE = 15, backend.h:52).
// At SCALE=3: 320×240 world pixels shown = 20×15 tiles on a 960×720 canvas.
export const RENDER_SCALE = 3;
const WORLD_W = Math.floor(VIEW_W / RENDER_SCALE); // 320 world px
const WORLD_H = Math.floor(VIEW_H / RENDER_SCALE); // 240 world px

interface SnapRef {
  snap: MergedWorldSnapshot;
  receivedAt: number;
}

// Interpolation runs on the SERVER TICK timeline, not arrival times: an EWMA
// clock model maps performance.now() onto ticks, and the render point trails
// the newest data by an adaptive delay (one tick + a jitter margin, see
// pickInterpPair). Late snapshots eat margin instead of freezing-then-
// snapping the way the old fixed 75 ms arrival-anchored delay did; a dry
// buffer extrapolates positions up to one tick before holding pose.
//
// Mirrors desktop/Assets/Scripts/Game/SnapshotInterpolator.cs — keep in sync.
//
// EWMA smoothing for the clock offset / jitter estimates (~10 snapshots to
// converge). A sample >10 ticks off the model is a re-sync, not jitter.
const CLOCK_ALPHA = 0.1;
const RESYNC_TICKS = 10;
// Ticks the render point may run past the newest snapshot on linear
// extrapolation before holding pose.
const EXTRAPOLATION_CAP_TICKS = 1;
// Jitter-margin clamp: at least half a tick, at most 150 ms so a bad spike
// can't push display latency into sluggish territory.
const JITTER_MARGIN_MIN_MS = TICK_MS * 0.5;
const JITTER_MARGIN_MAX_MS = 150;
// A tick regression larger than this means the room restarted — flush.
const TICK_REGRESSION_RESET = 20;
// Cap on retained snapshots — enough to bracket the render time under jitter.
const HISTORY_MAX = 10;

export class Renderer {
  // Global toggle for the name label above each tank. Mutated by the
  // burger menu's "Names" button, read by drawTank / drawTankFromSheet.
  // Static because there's only ever one Renderer instance per page and
  // both draw helpers live module-scope.
  static showTankNames = true;
  // Current visual theme. Refreshed by setMap(). The FFA-name-tint helper
  // reads this so FutureWar/Skynet rooms can suppress per-player colours
  // for non-self tanks — the FutureWar aesthetic is uniformly hostile
  // Skynet enemies.
  static currentTheme: Theme = Theme.Default;

  private readonly ctx: CanvasRenderingContext2D;
  private readonly atlas: HTMLCanvasElement;
  private sheet: HTMLCanvasElement | null = null;
  private modernSheet: HTMLCanvasElement | null = null;
  private graphicsMode: "classic" | "modern" = "classic";
  private map: LoadedMap | null = null;
  private mapCanvas: HTMLCanvasElement | null = null;
  private mapCtx: CanvasRenderingContext2D | null = null;
  private curr: SnapRef | null = null;
  // Snapshot history for delayed interpolation (oldest → newest).
  private readonly history: SnapRef[] = [];
  // Clock model: receivedAt ≈ tick * TICK_MS + clockOffsetMs. Jitter is the
  // EWMA absolute deviation of arrival samples from the model.
  private clockOffsetMs = 0;
  private clockJitterMs = 0;
  private hasClock = false;
  // Pooled interpolation output, reused every frame so the ~60fps render loop
  // doesn't allocate fresh arrays/objects per frame (a major GC-churn source).
  private readonly interpPlayers: PlayerState[] = [];
  private readonly interpShells: ShellState[] = [];
  private readonly interpBuilders: BuilderState[] = [];
  private readonly interpResult = {
    players: this.interpPlayers,
    shells: this.interpShells,
    builders: this.interpBuilders,
  };
  // Index maps over the current "from" snapshot, rebuilt only when that
  // snapshot changes (≈ once per tick) instead of on every frame.
  private idxFrom: SnapRef | null = null;
  private readonly fromPlayerById = new Map<number, PlayerState>();
  private readonly fromShellById = new Map<number, ShellState>();
  private readonly fromBuilderById = new Map<number, BuilderState>();
  private selfTeam: number = 0;

  constructor(public readonly canvas: HTMLCanvasElement) {
    canvas.width = VIEW_W;
    canvas.height = VIEW_H;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2D canvas context unavailable");
    this.ctx = ctx;
    this.atlas = buildTileAtlas();
    // Async load the WinBolo sprite sheet. While it's loading, render
    // continues against the procedural atlas; once ready, the map is
    // re-baked from the real tiles.
    loadSpriteSheet()
      .then((sheet) => {
        this.sheet = sheet;
        if (this.map) {
          const [c, mctx] = this.bakeMap(this.map);
          this.mapCanvas = c;
          this.mapCtx = mctx;
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[webolo] sprite sheet failed to load, using procedural art:", err);
      });
    loadModernSheet()
      .then((sheet) => {
        this.modernSheet = sheet;
        // Rebake the map with the modern tiles if that mode is already active.
        if (this.graphicsMode === "modern" && this.map) {
          const [c, mctx] = this.bakeMap(this.map);
          this.mapCanvas = c;
          this.mapCtx = mctx;
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[webolo] modern sprite sheet failed to load:", err);
      });
  }

  setMap(map: LoadedMap): void {
    this.map = map;
    Renderer.currentTheme = map.theme;
    const [canvas, mctx] = this.bakeMap(map);
    this.mapCanvas = canvas;
    this.mapCtx = mctx;
  }

  setSelfTeam(team: number): void {
    this.selfTeam = team;
  }

  setGraphicsMode(mode: "classic" | "modern"): void {
    if (this.graphicsMode === mode) return;
    this.graphicsMode = mode;
    // Rebake the terrain atlas with the new tile source.
    if (this.map) {
      const [c, mctx] = this.bakeMap(this.map);
      this.mapCanvas = c;
      this.mapCtx = mctx;
    }
  }

  getGraphicsMode(): "classic" | "modern" {
    return this.graphicsMode;
  }

  // Sprite scale helpers. Classic uses ctx.scale(3) so sprite source and
  // dest are both TILE_SIZE (16 px). Modern uses the 3× sheet: source
  // coords/size are ×3 but dest stays 16 px — ctx.scale(3) then scales
  // that 16 px dest to 48 px on screen, and nearest-neighbor resampling
  // from 48→16→48 is lossless for a pixel-perfect upscale.
  private get spriteF(): number { return this.graphicsMode === "modern" ? RENDER_SCALE : 1; }
  private get spriteC(): number { return TILE_SIZE * this.spriteF; }
  private get activeSheet(): HTMLCanvasElement | null {
    return this.graphicsMode === "modern" ? (this.modernSheet ?? this.sheet) : this.sheet;
  }

  patchTiles(updates: TileUpdate[]): void {
    if (!this.map || !this.mapCtx) return;
    // Apply all terrain changes first so neighbour resolution sees the
    // up-to-date map for every redrawn cell.
    for (const u of updates) {
      this.map.terrain[u.y * this.map.width + u.x] = u.terrain;
    }
    // Re-bake a 3x3 around each change because variant selection depends
    // on neighbours; flipping a single tile can shift the variants of
    // every tile that touched it.
    const dirty = new Set<number>();
    for (const u of updates) {
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const x = u.x + dx;
          const y = u.y + dy;
          if (x < 0 || y < 0 || x >= this.map.width || y >= this.map.height) continue;
          dirty.add(y * this.map.width + x);
        }
      }
    }
    for (const idx of dirty) {
      const x = idx % this.map.width;
      const y = (idx / this.map.width) | 0;
      const tile = this.map.terrain[idx] as Terrain;
      this.drawTerrainTile(this.mapCtx, tile, x * TILE_SIZE, y * TILE_SIZE, x, y);
    }
  }

  private storedSelfId: number | null = null;
  private explosions = new ExplosionTracker();
  readonly sound = new SoundSystem();
  private soundTracker = new SoundEventTracker();

  setSelfId(id: number | null): void {
    this.storedSelfId = id;
  }

  unlockAudio(): void {
    this.sound.unlock();
  }

  toggleMute(): boolean {
    this.sound.setMuted(!this.sound.isMuted);
    return this.sound.isMuted;
  }

  setSnapshot(snap: MergedWorldSnapshot): void {
    const prevSnap = this.curr?.snap ?? null;
    this.explosions.ingest(prevSnap, snap);

    // Sound event detection — requires map terrain for impact classification.
    const terrain = this.map?.terrain ?? null;
    const mapWidth = this.map?.width ?? 0;
    const selfId   = this.storedSelfId;
    const selfPlayer = snap.players.find((p) => p.id === selfId);
    const selfTileX  = selfPlayer ? Math.floor(selfPlayer.x / TILE_SIZE) : 0;
    const selfTileY  = selfPlayer ? Math.floor(selfPlayer.y / TILE_SIZE) : 0;

    const soundEvents = this.soundTracker.ingest(prevSnap, snap, selfId, terrain, mapWidth);
    for (const ev of soundEvents) {
      this.sound.playEvent(ev.type, ev.tileX, ev.tileY, selfTileX, selfTileY);
    }

    const receivedAt = performance.now();
    const newest = this.history[this.history.length - 1];
    if (newest && snap.tick <= newest.snap.tick) {
      // Out-of-order or duplicate. A large regression means the world
      // restarted its tick counter — start the buffer (and clock) over.
      if (newest.snap.tick - snap.tick > TICK_REGRESSION_RESET) {
        this.history.length = 0;
        this.hasClock = false;
      } else {
        return;
      }
    }
    this.curr = { snap, receivedAt };
    this.history.push(this.curr);
    if (this.history.length > HISTORY_MAX) this.history.shift();

    // Feed the clock model.
    const sample = receivedAt - snap.tick * TICK_MS;
    if (!this.hasClock) {
      this.clockOffsetMs = sample;
      this.clockJitterMs = 0;
      this.hasClock = true;
    } else {
      const err = sample - this.clockOffsetMs;
      if (Math.abs(err) > RESYNC_TICKS * TICK_MS) {
        this.clockOffsetMs = sample;
        this.clockJitterMs = 0;
      } else {
        this.clockOffsetMs += CLOCK_ALPHA * err;
        this.clockJitterMs += CLOCK_ALPHA * (Math.abs(err) - this.clockJitterMs);
      }
    }
  }

  // Camera top-left corner in world pixels for the most recent render.
  // Mouse input needs this to translate click coords back to world coords.
  private lastCamX = 0;
  private lastCamY = 0;

  getCamera(): { x: number; y: number } {
    return { x: this.lastCamX, y: this.lastCamY };
  }

  // playerId the camera should follow while the local tank is dead. null =
  // follow the local tank as usual. Set by the game screen's spectate logic.
  private spectateId: number | null = null;
  setSpectateId(id: number | null): void {
    this.spectateId = id;
  }

  renderOverlay(tileX: number, tileY: number, color: string): void {
    const ctx = this.ctx;
    const sx = tileX * TILE_SIZE - this.lastCamX;
    const sy = tileY * TILE_SIZE - this.lastCamY;
    ctx.save();
    ctx.scale(RENDER_SCALE, RENDER_SCALE);
    ctx.strokeStyle = color;
    ctx.lineWidth = 2 / RENDER_SCALE;
    ctx.globalAlpha = 0.8;
    ctx.strokeRect(sx + 1, sy + 1, TILE_SIZE - 2, TILE_SIZE - 2);
    ctx.globalAlpha = 0.18;
    ctx.fillStyle = color;
    ctx.fillRect(sx + 1, sy + 1, TILE_SIZE - 2, TILE_SIZE - 2);
    ctx.restore();
  }

  render(selfId: number | null): void {
    const ctx = this.ctx;
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, VIEW_W, VIEW_H);

    ctx.save();
    ctx.scale(RENDER_SCALE, RENDER_SCALE);
    ctx.imageSmoothingEnabled = false;

    const F = this.spriteF;
    const C = this.spriteC;
    const sheet = this.activeSheet;

    const interp = this.interpolate();

    // Camera follows the local tank, except while it's dead and a spectate
    // target is set (battle-royale / siege elimination) — then it follows the
    // chosen live player so the eliminated player can watch the match play out.
    const selfP = selfId != null ? interp.players.find((p) => p.id === selfId) : undefined;
    let camTarget = selfP;
    if ((!selfP || !selfP.alive) && this.spectateId != null) {
      const spec = interp.players.find((p) => p.id === this.spectateId);
      if (spec) camTarget = spec;
    }
    if (!camTarget) camTarget = interp.players[0];
    const camX = Math.round((camTarget ? camTarget.x : WORLD_W / 2) - WORLD_W / 2);
    const camY = Math.round((camTarget ? camTarget.y : WORLD_H / 2) - WORLD_H / 2);
    this.lastCamX = camX;
    this.lastCamY = camY;

    this.drawMap(camX, camY);

    // Mines render below shells and tanks so a tank drives visibly over them.
    if (this.curr) {
      for (const m of this.curr.snap.mines ?? []) {
        const mx = m.tileX * TILE_SIZE + TILE_SIZE / 2 - camX;
        const my = m.tileY * TILE_SIZE + TILE_SIZE / 2 - camY;
        if (mx < -8 || my < -8 || mx > WORLD_W + 8 || my > WORLD_H + 8) continue;
        if (sheet) {
          ctx.drawImage(
            sheet,
            MINE_SPRITE[0]*F, MINE_SPRITE[1]*F, C, C,
            mx - TILE_SIZE / 2, my - TILE_SIZE / 2, TILE_SIZE, TILE_SIZE,
          );
        } else {
          drawMine(ctx, mx, my, m, selfId);
        }
      }
      // Pillbox sprite/overlay rendering. With the sheet loaded we paint
      // the WinBolo pillbox tile (good/evil + armor variant) on top of
      // the baked terrain. Without the sheet, the procedural arc shows.
      for (const p of this.curr.snap.pillboxes ?? []) {
        const px = p.tileX * TILE_SIZE + TILE_SIZE / 2 - camX;
        const py = p.tileY * TILE_SIZE + TILE_SIZE / 2 - camY;
        if (px < -16 || py < -16 || px > WORLD_W + 16 || py > WORLD_H + 16) continue;
        if (sheet) {
          const isAllied = (p.owner > 0 && p.owner === selfId) ||
                           (p.team > 0 && p.team === this.selfTeam);
          const table = isAllied ? PILL_GOOD : PILL_EVIL;
          // Dropped pillbox uses the armor-0 (destroyed) sprite to signal it
          // is on the ground waiting for pickup, matching WinBolo's appearance.
          const armor = p.dropped ? 0 : Math.max(0, Math.min(15, p.armor));
          const [sx, sy] = table[armor] ?? table[0];
          ctx.drawImage(
            sheet,
            sx*F, sy*F, C, C,
            px - TILE_SIZE / 2, py - TILE_SIZE / 2, TILE_SIZE, TILE_SIZE,
          );
        } else {
          drawPillboxOverlay(ctx, px, py, p);
        }
      }
      for (const b of this.curr.snap.bases ?? []) {
        const bx = b.tileX * TILE_SIZE + TILE_SIZE / 2 - camX;
        const by = b.tileY * TILE_SIZE + TILE_SIZE / 2 - camY;
        if (bx < -16 || by < -16 || bx > WORLD_W + 16 || by > WORLD_H + 16) continue;
        if (sheet) {
          const isAllied = (b.owner > 0 && b.owner === selfId) ||
                           (b.team > 0 && b.team === this.selfTeam);
          const isNeutral = b.owner === 0 && b.team === 0;
          const [sx, sy] = isAllied ? BASE_GOOD : isNeutral ? BASE_NEUTRAL : BASE_EVIL;
          ctx.drawImage(
            sheet,
            sx*F, sy*F, C, C,
            bx - TILE_SIZE / 2, by - TILE_SIZE / 2, TILE_SIZE, TILE_SIZE,
          );
        } else {
          drawBaseOverlay(ctx, bx, by, b, selfId, this.selfTeam);
        }
      }
    }

    for (const s of interp.shells) {
      const sx = s.x - camX;
      const sy = s.y - camY;
      if (sx < -8 || sy < -8 || sx > WORLD_W + 8 || sy > WORLD_H + 8) continue;
      if (sheet) {
        // Sprite per direction. Sprites are not 16x16 — they're 3x4 or
        // 4x3 each, which is why we draw at the sprite's own w/h.
        // util.c:122 utilGetDir: nearest of 16 (sprite N covers (16N-8, 16N+8]).
        const idx = ((Math.floor((s.angle + 8) / 16) % 16) + 16) % 16;
        const sp = SHELL_SPRITES[idx];
        ctx.drawImage(
          sheet,
          sp.x*F, sp.y*F, sp.w*F, sp.h*F,
          sx - sp.w / 2, sy - sp.h / 2, sp.w, sp.h,
        );
      } else {
        ctx.fillStyle = "#ffe066";
        ctx.fillRect(sx - 2, sy - 2, 4, 4);
        ctx.strokeStyle = "#8a6a1e";
        ctx.lineWidth = 1;
        ctx.strokeRect(sx - 2, sy - 2, 4, 4);
      }
    }

    for (const p of interp.players) {
      const sx = p.x - camX;
      const sy = p.y - camY;
      if (sx < -24 || sy < -24 || sx > WORLD_W + 24 || sy > WORLD_H + 24) continue;
      const isSelf = p.id === selfId;
      if (sheet) {
        this.drawTankFromSheet(sx, sy, p, isSelf, sheet, F, C);
      } else {
        drawTank(ctx, sx, sy, p, isSelf);
      }
    }

    // Gunsight — drawn above tanks, only for the local player.
    const selfPlayer = interp.players.find((p) => p.id === selfId);
    if (selfPlayer && selfPlayer.alive) {
      const sx = selfPlayer.x - camX;
      const sy = selfPlayer.y - camY;
      const angle = (selfPlayer.angle / 256) * Math.PI * 2 - Math.PI / 2;
      const dist = (selfPlayer.sightLen / 2) * TILE_SIZE;
      const gx = sx + Math.cos(angle) * dist;
      const gy = sy + Math.sin(angle) * dist;
      if (sheet) {
        ctx.drawImage(
          sheet,
          GUNSIGHT_SPRITE[0]*F, GUNSIGHT_SPRITE[1]*F, C, C,
          gx - TILE_SIZE / 2, gy - TILE_SIZE / 2, TILE_SIZE, TILE_SIZE,
        );
      } else {
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(gx - 5, gy); ctx.lineTo(gx + 5, gy);
        ctx.moveTo(gx, gy - 5); ctx.lineTo(gx, gy + 5);
        ctx.stroke();
      }
    }

    for (const b of interp.builders) {
      const bx = b.x - camX;
      const by = b.y - camY;
      if (bx < -12 || by < -12 || bx > WORLD_W + 12 || by > WORLD_H + 12) continue;
      if (sheet) {
        this.drawBuilderFromSheet(bx, by, b, sheet, F);
      } else {
        drawBuilder(ctx, bx, by, b, b.playerId === selfId);
      }
    }

    // Explosion overlays — render last so they sit on top of everything.
    const now = performance.now();
    for (const e of this.explosions.active(now)) {
      const ex = e.x - camX;
      const ey = e.y - camY;
      if (ex < -16 || ey < -16 || ex > WORLD_W + 16 || ey > WORLD_H + 16) continue;
      const idx = ExplosionTracker.frameIndex(e, now);
      if (idx < 0) continue;
      if (sheet) {
        const [fx, fy] = EXPLOSION_FRAMES[idx];
        const scale = e.size === "large" ? 2 : 1;
        const w = TILE_SIZE * scale;
        const h = TILE_SIZE * scale;
        ctx.drawImage(sheet, fx*F, fy*F, C, C, ex - w / 2, ey - h / 2, w, h);
      } else {
        // Procedural fallback: ring that grows + fades.
        const t = idx / 8;
        ctx.strokeStyle = `rgba(255, 200, 80, ${1 - t})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        const r = (e.size === "large" ? 16 : 8) * (0.5 + t * 0.8);
        ctx.arc(ex, ey, r, 0, Math.PI * 2);
        ctx.stroke();
      }
    }

    // Battle-royale fog: a dense dark wall covering everything outside the
    // safe circle. Drawn on top of the world (tanks caught in it are obscured)
    // but inside the scale transform so it shares world-pixel coordinates.
    this.drawBattleRoyaleFog(camX, camY);

    if (!this.map) this.drawCenterText("Loading map...");

    ctx.restore();
  }

  // Renders the shrinking battle-royale safe zone as dense fog outside the
  // circle. Uses an even-odd fill (viewport rect minus the safe circle) so the
  // world stays visible inside the zone, a soft inner falloff so the wall reads
  // as rolling in, and a pulsing boundary ring so the edge is legible.
  private drawBattleRoyaleFog(camX: number, camY: number): void {
    const br = this.curr?.snap.battleRoyale;
    if (!br || br.phase !== "shrinking") return;
    const ctx = this.ctx;
    const cx = br.zoneX - camX;
    const cy = br.zoneY - camY;
    const r = Math.max(0, br.zoneRadius);

    ctx.save();
    // Dense fog everywhere outside the circle.
    ctx.beginPath();
    ctx.rect(0, 0, WORLD_W, WORLD_H);
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(6, 4, 12, 0.82)";
    ctx.fill("evenodd");

    // Soft falloff just inside the boundary so the wall of fog reads as
    // rolling in rather than a hard cut.
    if (r > 0) {
      const inner = Math.max(0, r - 14);
      const grad = ctx.createRadialGradient(cx, cy, inner, cx, cy, r);
      grad.addColorStop(0, "rgba(6, 4, 12, 0)");
      grad.addColorStop(1, "rgba(6, 4, 12, 0.5)");
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx.fill();
    }
    ctx.restore();
  }

  // Pick the two buffered snapshots that bracket the render tick and the
  // interpolation factor between them. `from`/`to` are the same object when
  // we can't bracket (startup), which yields a static pose rather than a
  // jump. `alpha` may exceed 1: that is the capped extrapolation zone past
  // the newest snapshot (positions extrapolate; rotation clamps — see
  // interpolate()).
  private pickInterpPair(): { from: SnapRef; to: SnapRef; alpha: number } | null {
    const hist = this.history;
    const n = hist.length;
    if (n === 0) return null;
    if (n === 1) return { from: hist[0], to: hist[0], alpha: 0 };
    // Render delay behind the estimated server timeline: one tick to have a
    // bracket to lerp inside, plus a margin of twice the observed jitter.
    const margin = Math.min(Math.max(2 * this.clockJitterMs, JITTER_MARGIN_MIN_MS), JITTER_MARGIN_MAX_MS);
    let renderTick = (performance.now() - this.clockOffsetMs - (TICK_MS + margin)) / TICK_MS;
    const newestTick = hist[n - 1].snap.tick;
    const oldestTick = hist[0].snap.tick;
    if (renderTick > newestTick + EXTRAPOLATION_CAP_TICKS) renderTick = newestTick + EXTRAPOLATION_CAP_TICKS;
    if (renderTick < oldestTick) renderTick = oldestTick;
    let hi = n - 1;
    for (let i = 1; i < n; i++) {
      if (hist[i].snap.tick >= renderTick) {
        hi = i;
        break;
      }
    }
    const from = hist[hi - 1];
    const to = hist[hi];
    const span = to.snap.tick - from.snap.tick;
    const alpha = span > 0 ? Math.max(0, (renderTick - from.snap.tick) / span) : 0;
    return { from, to, alpha };
  }

  // Rebuild the "from"-snapshot id indexes only when `from` changes, so the
  // per-frame path is index lookups + in-place writes with no allocation.
  private ensureFromIndex(from: SnapRef): void {
    if (this.idxFrom === from) return;
    this.idxFrom = from;
    this.fromPlayerById.clear();
    for (const p of from.snap.players) this.fromPlayerById.set(p.id, p);
    this.fromShellById.clear();
    for (const s of from.snap.shells) this.fromShellById.set(s.id, s);
    this.fromBuilderById.clear();
    for (const b of from.snap.builders) this.fromBuilderById.set(b.playerId, b);
  }

  private interpolate(): {
    players: InterpPlayer[];
    shells: InterpShell[];
    builders: InterpBuilder[];
  } {
    const players = this.interpPlayers;
    const shells = this.interpShells;
    const builders = this.interpBuilders;

    const pair = this.pickInterpPair();
    if (!pair) {
      players.length = 0;
      shells.length = 0;
      builders.length = 0;
      return this.interpResult;
    }
    const { from, to, alpha } = pair;
    this.ensureFromIndex(from);
    // Positions may extrapolate (alpha > 1, capped by pickInterpPair);
    // rotation never does — over-spinning a heading reads far worse than a
    // slightly stale one.
    const aRot = Math.min(1, alpha);

    const tp = to.snap.players;
    players.length = tp.length;
    for (let i = 0; i < tp.length; i++) {
      const cur = tp[i];
      const o = (players[i] ??= {} as PlayerState);
      Object.assign(o, cur);
      const p = this.fromPlayerById.get(cur.id);
      if (p) {
        o.x = lerp(p.x, cur.x, alpha);
        o.y = lerp(p.y, cur.y, alpha);
        o.dir = lerpDir(p.dir, cur.dir, aRot);
        o.angle = lerpAngle(p.angle, cur.angle, aRot);
      }
    }

    const ts = to.snap.shells;
    shells.length = ts.length;
    for (let i = 0; i < ts.length; i++) {
      const cur = ts[i];
      const o = (shells[i] ??= {} as ShellState);
      Object.assign(o, cur);
      const p = this.fromShellById.get(cur.id);
      if (p) {
        o.x = lerp(p.x, cur.x, alpha);
        o.y = lerp(p.y, cur.y, alpha);
        o.angle = lerpAngle(p.angle, cur.angle, aRot);
      }
    }

    const tb = to.snap.builders;
    builders.length = tb.length;
    for (let i = 0; i < tb.length; i++) {
      const cur = tb[i];
      const o = (builders[i] ??= {} as BuilderState);
      Object.assign(o, cur);
      const p = this.fromBuilderById.get(cur.playerId);
      if (p) {
        o.x = lerp(p.x, cur.x, alpha);
        o.y = lerp(p.y, cur.y, alpha);
      }
    }

    return this.interpResult;
  }

  private drawMap(camX: number, camY: number): void {
    if (!this.mapCanvas) return;
    const sx = camX;
    const sy = camY;
    const sw = VIEW_W;
    const sh = VIEW_H;
    const ctx = this.ctx;
    const mx = Math.max(0, sx);
    const my = Math.max(0, sy);
    const mw = Math.min(this.mapCanvas.width - mx, sw + Math.min(0, sx));
    const mh = Math.min(this.mapCanvas.height - my, sh + Math.min(0, sy));
    if (mw > 0 && mh > 0) {
      ctx.drawImage(this.mapCanvas, mx, my, mw, mh, mx - sx, my - sy, mw, mh);
    }
  }

  private bakeMap(map: LoadedMap): [HTMLCanvasElement, CanvasRenderingContext2D] {
    const c = document.createElement("canvas");
    c.width = map.width * TILE_SIZE;
    c.height = map.height * TILE_SIZE;
    const ctx = c.getContext("2d");
    if (!ctx) throw new Error("baked map ctx unavailable");
    for (let y = 0; y < map.height; y++) {
      for (let x = 0; x < map.width; x++) {
        const tile = map.terrain[y * map.width + x] as Terrain;
        this.drawTerrainTile(ctx, tile, x * TILE_SIZE, y * TILE_SIZE, x, y);
      }
    }
    return [c, ctx];
  }

  // Reads the 8 surrounding tiles around (tileX, tileY). Out-of-bounds
  // neighbours report DeepSea so the world's edge looks like open ocean.
  private neighborsAt(tileX: number, tileY: number): Neighbors {
    const m = this.map;
    if (!m) {
      const D = Terrain.DeepSea;
      return { aboveLeft: D, above: D, aboveRight: D, left: D, right: D, belowLeft: D, below: D, belowRight: D };
    }
    const get = (x: number, y: number): Terrain => {
      if (x < 0 || y < 0 || x >= m.width || y >= m.height) return Terrain.DeepSea;
      return m.terrain[y * m.width + x] as Terrain;
    };
    return {
      aboveLeft:  get(tileX - 1, tileY - 1),
      above:      get(tileX,     tileY - 1),
      aboveRight: get(tileX + 1, tileY - 1),
      left:       get(tileX - 1, tileY),
      right:      get(tileX + 1, tileY),
      belowLeft:  get(tileX - 1, tileY + 1),
      below:      get(tileX,     tileY + 1),
      belowRight: get(tileX + 1, tileY + 1),
    };
  }

  // Draws a single terrain tile from the WinBolo sprite sheet if it's
  // loaded, otherwise from the procedural atlas. For DeepSea / River /
  // Forest we pick a neighbour-aware variant per WinBolo's screencalc.
  private drawTerrainTile(
    ctx: CanvasRenderingContext2D,
    tile: Terrain,
    dx: number,
    dy: number,
    tileX: number,
    tileY: number,
  ): void {
    // Pick the active sprite sheet and scale factor for source coords.
    const activeSheet = this.graphicsMode === "modern"
      ? (this.modernSheet ?? this.sheet)
      : this.sheet;
    const F = this.spriteF;
    const C = this.spriteC; // source cell size in sheet pixels

    if (activeSheet) {
      let src: readonly [number, number] | undefined;
      if (tile === Terrain.DeepSea) {
        src = variantForDeepSea(this.neighborsAt(tileX, tileY));
      } else if (tile === Terrain.River) {
        src = variantForRiver(this.neighborsAt(tileX, tileY));
      } else if (tile === Terrain.Forest) {
        src = variantForForest(this.neighborsAt(tileX, tileY));
      } else if (tile === Terrain.Wall || tile === Terrain.HalfBuiltWall) {
        // HalfBuiltWall threads into walls per the source's HALFBUILDING
        // rewrite. We render the half-built sprite for the half tile
        // itself but compute its neighbour-aware shape the same way.
        src = variantForBuilding(this.neighborsAt(tileX, tileY));
      } else if (tile === Terrain.Road) {
        src = variantForRoad(this.neighborsAt(tileX, tileY));
      } else if (tile === Terrain.Boat) {
        src = variantForBoat(this.neighborsAt(tileX, tileY));
      } else {
        src = TERRAIN_TILE[tile];
      }
      if (src) {
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(
          activeSheet,
          src[0] * F, src[1] * F, C, C,
          dx, dy, TILE_SIZE, TILE_SIZE,
        );
        return;
      }
    }
    ctx.drawImage(
      this.atlas,
      tile * TILE_SIZE, 0, TILE_SIZE, TILE_SIZE,
      dx, dy, TILE_SIZE, TILE_SIZE,
    );
  }

  private drawCenterText(text: string): void {
    const ctx = this.ctx;
    ctx.fillStyle = "#e6edf3";
    ctx.font = `${20 / RENDER_SCALE}px system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, WORLD_W / 2, WORLD_H / 2);
  }

  // Builder ("LGM" — little green man) from the sprite sheet. Walking
  // animation cycles through the three frames at ~150 ms each. Sprites
  // are 3x8; we upscale x2 so they're visible at game scale.
  // During parachute we render a procedural helicopter instead — the
  // shipped tile sheet doesn't include a helicopter sprite (the WinBolo
  // location at 256,112 is a pillbox here), and a 3-px running figure
  // streaking across the map at chopper speed read as "TLM teleports
  // back" before this change.
  private drawBuilderFromSheet(cx: number, cy: number, b: BuilderState, sheet: HTMLCanvasElement, F: number): void {
    if (!b.alive) return;
    const ctx = this.ctx;
    if (b.parachuting) {
      drawHelicopter(ctx, cx, cy, b);
      return;
    }
    // Phase the animation off the builder's position so two builders
    // don't march in lockstep.
    const t = (performance.now() / 150 + b.x * 0.7 + b.y * 0.3) | 0;
    const frame = LGM_FRAMES[t % LGM_FRAMES.length];
    const scale = 2;
    const w = frame.w * scale;
    const h = frame.h * scale;
    ctx.drawImage(sheet, frame.x*F, frame.y*F, frame.w*F, frame.h*F, cx - w / 2, cy - h / 2, w, h);
    // Working ring stays — it's a UI tell, not a sprite.
    if (b.busy) {
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(cx, cy, 7, 0, Math.PI * 2);
      ctx.stroke();
    }
  }

  // Picks the right WinBolo tank skin (Self / Good / Evil, with a Boat
  // variant if onBoat) and blits it from the sprite sheet.
  private drawTankFromSheet(
    cx: number, cy: number, p: PlayerState, isSelf: boolean,
    sheet: HTMLCanvasElement, F: number, C: number,
  ): void {
    const ctx = this.ctx;
    const idx = dirToSpriteIndex(p.dir);
    // In team play, allied = same non-zero team. In FFA every non-self is
    // hostile, which already makes `allied` false here.
    const allied = !isSelf && p.team > 0 && p.team === this.selfTeam;
    let table: readonly (readonly [number, number])[];
    if (isSelf) {
      table = p.onBoat ? TANK_SELFBOAT : TANK_SELF;
    } else if (allied) {
      table = p.onBoat ? TANK_GOODBOAT : TANK_GOOD;
    } else {
      table = p.onBoat ? TANK_EVILBOAT : TANK_EVIL;
    }
    const [sx, sy] = table[idx] ?? [0, 0];
    if (!p.alive) ctx.globalAlpha = 0.4;
    ctx.drawImage(
      sheet,
      sx * F, sy * F, C, C,
      cx - TILE_SIZE / 2, cy - TILE_SIZE / 2, TILE_SIZE, TILE_SIZE,
    );
    if (!p.alive) ctx.globalAlpha = 1;

    if (Renderer.showTankNames) {
      ctx.fillStyle = nameColor(p, isSelf);
      ctx.font = `${11 / RENDER_SCALE}px system-ui, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "alphabetic";
      ctx.fillText(p.name, cx, cy - 30 / RENDER_SCALE);
    }
  }
}

type InterpPlayer = PlayerState;
type InterpShell = ShellState;
type InterpBuilder = BuilderState;

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpDir(a: number, b: number, t: number): number {
  let diff = b - a;
  if (diff > TANK_DIRECTIONS / 2) diff -= TANK_DIRECTIONS;
  else if (diff < -TANK_DIRECTIONS / 2) diff += TANK_DIRECTIONS;
  return (a + diff * t + TANK_DIRECTIONS) % TANK_DIRECTIONS;
}

// Shortest-path lerp for continuous 0..256 bradian angles.
// At 20 Hz the angle changes at most 2.5 brad/tick, well under 128,
// so the short path is always the correct one.
function lerpAngle(a: number, b: number, t: number): number {
  let diff = b - a;
  if (diff > 128) diff -= 256;
  else if (diff < -128) diff += 256;
  return ((a + diff * t) + 256) % 256;
}

function drawTank(ctx: CanvasRenderingContext2D, cx: number, cy: number, p: PlayerState, isSelf: boolean): void {
  const angle = (p.dir / TANK_DIRECTIONS) * Math.PI * 2 - Math.PI / 2;
  // Team colour drives the hull; self-tank stays accent orange so you can
  // find it in a crowd. FFA picked colours are surfaced via the name label
  // (drawn separately) — keeping the tank silhouette familiar matters more
  // than reflecting the colour pick on the hull.
  const hullColor = isSelf
    ? "#d89634"
    : (p.team > 0 ? TEAM_COLORS[p.team] : "#3a6ac0");
  const turretColor = isSelf ? "#b87a1f" : shade(hullColor, -0.25);
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(angle + Math.PI / 2);
  if (!p.alive) ctx.globalAlpha = 0.4;
  // Boat hull underneath the tank when the tank is currently sailing.
  // Drawn in local (rotated) coordinates so it tracks tank facing.
  if (p.onBoat) {
    ctx.fillStyle = "#8b5a2b";
    ctx.beginPath();
    ctx.moveTo(-10, -11);
    ctx.lineTo(10, -11);
    ctx.lineTo(8, 11);
    ctx.lineTo(-8, 11);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = "#3a2410";
    ctx.lineWidth = 1;
    ctx.stroke();
  }
  ctx.fillStyle = "#1a1a1a";
  ctx.fillRect(-9, -9, 4, 18);
  ctx.fillRect(5, -9, 4, 18);
  ctx.fillStyle = hullColor;
  ctx.fillRect(-6, -8, 12, 16);
  ctx.strokeStyle = "#101010";
  ctx.lineWidth = 1;
  ctx.strokeRect(-6, -8, 12, 16);
  ctx.fillStyle = turretColor;
  ctx.beginPath();
  ctx.arc(0, 0, 4, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = "#222";
  ctx.fillRect(-1, -10, 2, 8);
  ctx.restore();

  if (Renderer.showTankNames) {
    ctx.fillStyle = nameColor(p, isSelf);
    ctx.font = "11px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    ctx.fillText(p.name, cx, cy - 14);
  }
}

// Tint for the name label drawn above each tank. FFA tanks with a picked
// colorIndex carry that colour up to their name so the swatch is visible
// without modifying the tank sprite itself. Team-mode tanks colour the name
// in their team palette; unpicked FFA tanks use the default bone text.
//
// FutureWar/Skynet rooms suppress the per-player FFA tint for everyone
// except the local player — the theme is built around a uniform Skynet
// army, so individual enemy hues would break the visual contract. Self
// keeps their pick so the player still recognises their own tank.
function nameColor(p: PlayerState, isSelf: boolean): string {
  if (p.team > 0) return TEAM_COLORS[p.team] ?? "#e6edf3";
  const ffa = p.colorIndex !== undefined && p.colorIndex >= 0 && p.colorIndex < TEAM_COLORS.length
    ? TEAM_COLORS[p.colorIndex]
    : null;
  const isFutureWar = Renderer.currentTheme === Theme.FutureWar;
  if (ffa !== null && (isSelf || !isFutureWar)) return ffa;
  return "#e6edf3";
}

// Darkens/lightens a hex color for the turret accent. Keeps the two
// shades visually coupled to the hull color without needing a second
// palette.
function shade(hex: string, pct: number): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return hex;
  const mix = (c: number) => Math.max(0, Math.min(255, Math.round(c * (1 + pct))));
  const r = mix(parseInt(m[1], 16));
  const g = mix(parseInt(m[2], 16));
  const b = mix(parseInt(m[3], 16));
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

function drawBuilder(ctx: CanvasRenderingContext2D, x: number, y: number, b: BuilderState, isOwn: boolean): void {
  if (!b.alive) return;
  if (b.parachuting) { drawHelicopter(ctx, x, y, b); return; }
  ctx.fillStyle = isOwn ? "#ffe066" : "#aabbcc";
  ctx.beginPath();
  ctx.arc(x, y, 3, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#202020";
  ctx.lineWidth = 1;
  ctx.stroke();
  if (b.busy) {
    ctx.strokeStyle = "#ffffff";
    ctx.beginPath();
    ctx.arc(x, y, 6, 0, Math.PI * 2);
    ctx.stroke();
  }
}

// Procedural top-down helicopter for the LGM respawn cinematic. The
// shipped tile sheet (winbolo tiles.bmp) doesn't include a helicopter
// sprite, so we draw one with primitives — fuselage + cockpit + tail
// boom + tail rotor + a spinning two-blade main rotor on top + a
// downward spotlight cone pointed at the drop-off point. Sizing is
// chosen so the rig reads at a glance against terrain tiles but doesn't
// overwhelm the LGM-on-cable beneath it. Aim the spotlight at the
// drop-off when the snapshot carries one (b.destX/destY), otherwise
// shine it straight down on the LGM (the follow-walk phase isn't a
// thing on the web client since the helicopter view ends with parachute,
// but render-safe is render-safe).
function drawHelicopter(ctx: CanvasRenderingContext2D, x: number, y: number, b: BuilderState): void {
  // Spin phase off wall-clock so a stalled snapshot doesn't freeze the
  // blades. ~10 revs/sec.
  const tNow = performance.now();
  const rotorPhase = (tNow * 0.020) % (Math.PI * 2);

  // Heading derived from the dropoff vector — if we have one, the
  // helicopter noses toward where it's delivering TLM.
  let yawRad = 0;
  if (typeof b.destX === "number" && typeof b.destY === "number") {
    const dx = b.destX - b.x;
    const dy = b.destY - b.y;
    if (dx * dx + dy * dy > 1) yawRad = Math.atan2(dy, dx);
  }

  // Spotlight pool on the ground beneath the drop point. Draw FIRST so
  // the helicopter blades aren't underneath the glow. If we have a
  // dropoff point we render the cone from the chopper to it; otherwise
  // a circular pool right under the helicopter.
  ctx.save();
  if (typeof b.destX === "number" && typeof b.destY === "number") {
    const dx = b.destX - b.x;
    const dy = b.destY - b.y;
    const distSq = dx * dx + dy * dy;
    if (distSq > 0.5) {
      const dist = Math.sqrt(distSq);
      // Cone narrows from the helicopter to a small puddle at the dest.
      const ux = dx / dist, uy = dy / dist;
      // Left/right perpendicular at the helicopter end (wide).
      const px = -uy, py = ux;
      const widthAtHeli = 3;
      const widthAtDest = 7;
      const heliEndX = x;
      const heliEndY = y;
      const destEndX = x + ux * Math.min(dist, 80); // cap the visible cone length
      const destEndY = y + uy * Math.min(dist, 80);
      const grad = ctx.createLinearGradient(heliEndX, heliEndY, destEndX, destEndY);
      grad.addColorStop(0, "rgba(255, 240, 200, 0.55)");
      grad.addColorStop(1, "rgba(255, 240, 200, 0.05)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.moveTo(heliEndX + px * widthAtHeli, heliEndY + py * widthAtHeli);
      ctx.lineTo(destEndX + px * widthAtDest, destEndY + py * widthAtDest);
      ctx.lineTo(destEndX - px * widthAtDest, destEndY - py * widthAtDest);
      ctx.lineTo(heliEndX - px * widthAtHeli, heliEndY - py * widthAtHeli);
      ctx.closePath();
      ctx.fill();
      // Small bright spot on the ground at the drop point.
      const groundGrad = ctx.createRadialGradient(b.destX, b.destY, 0, b.destX, b.destY, 9);
      groundGrad.addColorStop(0, "rgba(255, 240, 200, 0.45)");
      groundGrad.addColorStop(1, "rgba(255, 240, 200, 0)");
      ctx.fillStyle = groundGrad;
      ctx.beginPath();
      ctx.arc(b.destX, b.destY, 9, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(yawRad + Math.PI / 2); // sprite is drawn nose-up; rotate so nose tracks heading

  // Fuselage — small grey rounded rectangle.
  ctx.fillStyle = "#3a4356";
  ctx.strokeStyle = "#10141c";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.ellipse(0, -1, 4, 5.5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  // Cockpit canopy — tinted glassy front, slightly forward.
  ctx.fillStyle = "#7ec9ff";
  ctx.beginPath();
  ctx.ellipse(0, -4, 2.5, 2.3, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  // Tail boom + vertical fin.
  ctx.fillStyle = "#3a4356";
  ctx.fillRect(-0.7, 4, 1.4, 7);
  ctx.strokeRect(-0.7, 4, 1.4, 7);
  ctx.fillRect(-2, 9, 4, 1.5);
  ctx.strokeRect(-2, 9, 4, 1.5);

  // Tail rotor — small spinning blade on the side of the fin.
  const tailRotor = (tNow * 0.06) % (Math.PI * 2);
  ctx.save();
  ctx.translate(1.6, 10);
  ctx.rotate(tailRotor);
  ctx.fillStyle = "#10141c";
  ctx.fillRect(-2, -0.3, 4, 0.6);
  ctx.restore();

  // Skids — two horizontal bars under the fuselage.
  ctx.strokeStyle = "#1a1f2c";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(-3.5, 3); ctx.lineTo(-3.5, -3.5);
  ctx.moveTo( 3.5, 3); ctx.lineTo( 3.5, -3.5);
  ctx.stroke();

  // Main rotor — two crossed blades spinning around the centre.
  // Render two blade pairs at different phases for a faint motion-blur.
  ctx.strokeStyle = "rgba(20, 22, 30, 0.65)";
  ctx.lineWidth = 1.4;
  for (let i = 0; i < 2; i++) {
    const phase = rotorPhase + i * Math.PI * 0.5;
    const c = Math.cos(phase), s = Math.sin(phase);
    ctx.beginPath();
    ctx.moveTo(-12 * c, -12 * s);
    ctx.lineTo( 12 * c,  12 * s);
    ctx.moveTo(-12 * s,  12 * c);
    ctx.lineTo( 12 * s, -12 * c);
    ctx.stroke();
  }

  // Nav lights — red port (left), green starboard (right), and a small
  // blinking strobe on the tail.
  ctx.fillStyle = "#ff4040";
  ctx.beginPath(); ctx.arc(-3.6, 0, 0.9, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = "#40ff40";
  ctx.beginPath(); ctx.arc( 3.6, 0, 0.9, 0, Math.PI * 2); ctx.fill();
  const strobeOn = (tNow % 700) < 100;
  ctx.fillStyle = strobeOn ? "#ffffff" : "#444444";
  ctx.beginPath(); ctx.arc(0, 10.5, 0.9, 0, Math.PI * 2); ctx.fill();

  ctx.restore();

  // Cable + LGM dangling beneath the helicopter while in transit. Hide
  // when the chopper is right over the drop point (we're about to flip
  // to walking, no need for a cable visual).
  if (typeof b.destX === "number" && typeof b.destY === "number") {
    const dx = b.destX - b.x;
    const dy = b.destY - b.y;
    if (dx * dx + dy * dy > 16) {
      ctx.strokeStyle = "rgba(80, 80, 80, 0.75)";
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(x, y + 1);
      ctx.lineTo(x, y + 9);
      ctx.stroke();
      ctx.fillStyle = "#e0c97a";
      ctx.beginPath();
      ctx.arc(x, y + 10, 1.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#202020";
      ctx.lineWidth = 0.5;
      ctx.stroke();
    }
  }
}

function drawMine(ctx: CanvasRenderingContext2D, x: number, y: number, _m: MineState, _selfId: number | null): void {
  // All mines visible for now (no fog of war yet).
  ctx.fillStyle = "#1a1a1a";
  ctx.beginPath();
  ctx.arc(x, y, 3, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#ff4d4d";
  ctx.lineWidth = 1;
  ctx.stroke();
}

function drawPillboxOverlay(ctx: CanvasRenderingContext2D, x: number, y: number, p: PillboxState): void {
  if (p.dropped) {
    // Dropped pillbox: dashed ring on the ground to invite pickup.
    ctx.save();
    ctx.setLineDash([3, 2]);
    ctx.strokeStyle = "#e6edf3";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(x, y, 5, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
    return;
  }
  // Armor arc around the pillbox as a quick health indicator.
  const pct = p.armor / 15;
  ctx.strokeStyle = pct > 0.5 ? "#7ee787" : pct > 0.25 ? "#f2c744" : "#ff6b6b";
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(x, y, 7, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * pct);
  ctx.stroke();
}

function drawBaseOverlay(
  ctx: CanvasRenderingContext2D, x: number, y: number,
  b: BaseState, selfId: number | null, selfTeam: number,
): void {
  const isAllied = (b.owner > 0 && b.owner === selfId) ||
                   (b.team > 0 && b.team === selfTeam);
  const isNeutral = b.owner === 0 && b.team === 0;
  const color = isAllied ? "#7ee787" : isNeutral ? "#f2c744" : "#ff6b6b";
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(x, y, 7, 0, Math.PI * 2);
  ctx.stroke();
}

// Keep Terrain import used by TS even if not referenced directly here.
export type _KeepTerrain = Terrain;
